namespace $.$$ {

	export class $shm_hitalama_map_pane_tool_save extends $mol_object {
		run(): void {}
		snapshot(): $shm_hitalama_map_pane_tool_snapshot | null { return null }

		@ $mol_mem
		widget( next?: $shm_hitalama_map_widget ) { return next }

		@ $mol_mem
		saved() {
			this.run()
			return true
		}
	}

	type $shm_hitalama_map_pane_tool_mode = '' | 'drawing' | 'rect' | 'circle'
	type $shm_hitalama_map_pane_tool_point = readonly [ number, number ]
	type $shm_hitalama_map_pane_tool_box = {
		readonly left: number
		readonly top: number
		readonly width: number
		readonly height: number
	}
	type $shm_hitalama_map_pane_tool_snapshot =
		| { readonly mode: 'drawing', readonly line: $shm_hitalama_map_widget_entity_drawing_line }
		| { readonly mode: 'rect' | 'circle', readonly box: $shm_hitalama_map_pane_tool_box }

	export class $shm_hitalama_map_pane_tool extends $.$shm_hitalama_map_pane_tool {

		start_listener: $mol_dom_listener | null = null
		move_listener: $mol_dom_listener | null = null
		up_listener: $mol_dom_listener | null = null
		cancel_listener: $mol_dom_listener | null = null
		private drawing_pointer: number | null = null

		@ $mol_mem
		save_queue( next?: readonly $shm_hitalama_map_pane_tool_save[] ): readonly $shm_hitalama_map_pane_tool_save[] {
			return next ?? []
		}

		@ $mol_mem
		pending_drawings( next?: readonly $shm_hitalama_map_pane_tool_save[] ): readonly $shm_hitalama_map_pane_tool_save[] {
			return next ?? []
		}

		@ $mol_mem
		preview_jobs() {
			// The widget's existing view channel is set after its first render.
			// Retain the local drawing until the permanent widget takes its place.
			return this.pending_drawings().filter( job => !job.widget()?.view() )
		}

		@ $mol_action
		previews_prune( visible: readonly $shm_hitalama_map_pane_tool_save[] ) {
			if( visible.length !== this.pending_drawings().length ) this.pending_drawings( visible )
		}

		@ $mol_action
		save_flush() {
			while( this.save_queue().length ) {
				const job = this.save_queue()[0]
				job.saved()
				this.save_queue( this.save_queue().filter( pending => pending !== job ) )
			}
		}

		auto() {
			return [
				...super.auto(),
				this.listen_start(),
				this.previews_prune( this.preview_jobs() ),
				this.save_queue().length ? this.save_flush() : null,
			]
		}

		@ $mol_mem
		listen_start() {
			this.start_listener?.destructor()
			this.start_listener = null
			if( !this.enabled() ) return null

			this.start_listener = new $mol_dom_listener(
				this.dom_node(),
				'pointerdown',
				$mol_wire_async( event => this.event_start( event as PointerEvent ) ),
				{ passive: false, capture: true } as any,
			)
			return this.start_listener
		}

		enabled() {
			return this.mode() !== ''
		}

		pointer_events() {
			return this.enabled() ? 'auto' : 'none'
		}

		event_start( event?: PointerEvent | null ) {
			if( !this.enabled() ) return
			if( !event ) return
			if( event.button !== 0 ) return
			event.preventDefault()
			event.stopPropagation()
			event.stopImmediatePropagation()
			if( this.drafting() ) return

			const point = $mol_wire_sync( this ).point( event )
			if( this.mode() === 'text' || this.mode() === 'text_md' ) {
				$mol_wire_sync( this ).text_create( event, point, this.mode() as 'text' | 'text_md' )
				return
			}
			this.drawing_pointer = event.pointerId
			if( this.mode() === 'drawing' ) this.draw_start( point )
			else this.shape_start( point )

			if( this.drafting() ) this.listen_document()
		}

		event_move( event?: PointerEvent | null ) {
			if( !this.enabled() ) return
			if( !event ) return
			if( this.drawing_pointer !== null && event.pointerId !== this.drawing_pointer ) return
			if( !this.drafting() ) return
			event.preventDefault()
			if( this.draft_frozen() ) return

			const point = this.point( event )
			if( this.mode() === 'drawing' ) this.draw( point )
			else this.shape_update( point )
		}


		event_end( event?: PointerEvent | null ) {
			if( event && this.drawing_pointer !== null && event.pointerId !== this.drawing_pointer ) return
			this.unlisten_document()
			if( !this.enabled() ) return
			if( !event ) return
			if( this.drafting() ) {
				event.preventDefault()
				event.stopPropagation()
				const point = $mol_wire_sync(this).point( event )
				const operation = $shm_hitalama_map_event_key( event, 'drawing-tool-create' )
				if( this.mode() === 'drawing' ) this.draw_end( point, operation )
				else this.shape_end( point, operation )
			}
		}

		listen_document() {
			this.unlisten_document()
			
			const document = this.$.$mol_dom_context.document
			this.move_listener = new $mol_dom_listener(
				document,
				'pointermove',
				$mol_wire_async( event => this.event_move( event as PointerEvent ) ),
				{ passive: false },
			)
			this.up_listener = new $mol_dom_listener(
				document,
				'pointerup',
				$mol_wire_async( event => this.event_end( event as PointerEvent ) ),
				{ passive: false },
			)
			this.cancel_listener = new $mol_dom_listener(
				document,
				'pointercancel',
				event => {
					if( this.drawing_pointer === null || event.pointerId === this.drawing_pointer ) this.draft_cancel()
				},
			)
		}

		@ $mol_action
		draft_cancel() {
			this.unlisten_document()
			this.drawing_pointer = null
			this.draft_line( null )
			this.draft_start( null )
			this.draft_end( null )
			this.draft_frozen( false )
		}

		unlisten_document() {
			this.move_listener?.destructor()
			this.up_listener?.destructor()
			this.cancel_listener?.destructor()
			this.move_listener = null
			this.up_listener = null
			this.cancel_listener = null
		}

		point( event: PointerEvent ): $shm_hitalama_map_pane_tool_point {
			const pane_point = this.client_pos_to_pane_pos([ event.clientX, event.clientY ])
			const real_point = this.to_real_pos( pane_point )
			return [ real_point[0], real_point[1] ]
		}

		drafting() {
			return Boolean( this.draft_line() || this.draft_start() )
		}

		draw_start( point: $shm_hitalama_map_pane_tool_point ) {
			this.draft_line({
				color: '#808080',
				width: 4,
				points: [ point ],
			})
		}

		draw( point: $shm_hitalama_map_pane_tool_point ) {
			const line = this.draft_line() as $shm_hitalama_map_widget_entity_drawing_line | null
			if( !line ) return

			const prev = line.points.at( -1 )
			if( prev && this.distance_less( prev, point, 2 ) ) return

			this.draft_line({
				... line,
				points: [ ... line.points, point ],
			})
		}

		@ $mol_action
		draw_end( point: $shm_hitalama_map_pane_tool_point, operation?: string ) {
			this.draw( point )
			const line = this.draft_line() as $shm_hitalama_map_widget_entity_drawing_line | null
			// Detach the completed snapshot before persistence can suspend. A new
			// pointerdown must never wait for storage or an animation frame.
			this.draft_cancel()
			if( line ) this.preview_enqueue({ mode: 'drawing', line }, operation)
		}

		@ $mol_action
		preview_enqueue( snapshot: $shm_hitalama_map_pane_tool_snapshot, operation?: string ) {
			const job = $shm_hitalama_map_pane_tool_save.make({
				snapshot: ()=> snapshot,
				run: ()=> snapshot.mode === 'drawing'
					? this.drawing_create( snapshot.line, operation, job )
					: this.shape_create( snapshot.mode, snapshot.box, operation, job ),
			})
			this.pending_drawings([ ...this.pending_drawings(), job ])
			this.save_queue([ ...this.save_queue(), job ])
		}

		shape_start( point: $shm_hitalama_map_pane_tool_point ) {
			this.draft_start( point )
			this.draft_end( point )
		}

		shape_update( point: $shm_hitalama_map_pane_tool_point ) {
			this.draft_end( point )
		}

		shape_end( point: $shm_hitalama_map_pane_tool_point, operation?: string ) {
			this.shape_update( point )
			const mode = this.mode() as $shm_hitalama_map_pane_tool_mode
			const box = this.shape_box( true )
			this.draft_cancel()
			if( box && ( mode === 'rect' || mode === 'circle' ) ) this.preview_enqueue({ mode, box }, operation)
		}

		distance_less(
			prev: $shm_hitalama_map_widget_entity_drawing_point,
			next: $shm_hitalama_map_widget_entity_drawing_point,
			limit: number,
		) {
			const x = next[0] - prev[0]
			const y = next[1] - prev[1]
			return x * x + y * y < limit * limit
		}

		@ $mol_action
		text_create( event: PointerEvent, point: $shm_hitalama_map_pane_tool_point, mode: 'text' | 'text_md' ) {
			return $shm_hitalama_map_event_once( event, 'text-tool-create', ()=> {
				const page = this.Map_page()
				const widget = page.map().widget_add( mode, point, 320, 180, undefined, mode === 'text_md' ? '# Text' : 'text', undefined, $shm_hitalama_map_event_key( event, 'text-tool-create' ) )!
				page.selected_links([ widget.link() ])
				page.tool_mode( '' )
				if( mode === 'text_md' ) {
					page.widget_props_open( widget, 'text' )
					new $mol_after_frame( ()=> $mol_wire_async( page.Widget_props() ).text_focus() )
				}
				else new $mol_after_frame( ()=> {
					const view = page.Widget( widget.link() ).Sub() as $shm_hitalama_map_widget_float_text
					$mol_wire_async( view ).edit_start()
				} )
				return widget
			} )
		}

		drawing_initialize( line: $shm_hitalama_map_widget_entity_drawing_line, preview?: $shm_hitalama_map_pane_tool_save ) {
			const lines = [ line ]
			return ( widget: $shm_hitalama_map_widget )=> {
				widget.background_color( 'transparent' )
				widget.drawing().lines( lines )
				preview?.widget( widget )
			}
		}

		drawing_create( line: $shm_hitalama_map_widget_entity_drawing_line, operation = '', preview?: $shm_hitalama_map_pane_tool_save ) {
			const box = this.line_box( line )
			if( !box ) return

			const pos = $mol_wire_sync( Array ).of( box.left, box.top ) as number[]
			const initialize = $mol_wire_sync( this ).drawing_initialize( line, preview )
			this.Map_page().map().widget_add( 'drawing', pos, box.width, box.height, undefined, '', undefined, operation, initialize )
		}

		shape_initialize( preview?: $shm_hitalama_map_pane_tool_save ) {
			return ( widget: $shm_hitalama_map_widget )=> {
				widget.background_color( 'transparent' )
				widget.Border_color(null)?.val( '#808080' )
				widget.Border_width(null)?.val( 4 )
				widget.Border_style(null)?.val( 'solid' )
				preview?.widget( widget )
			}
		}

		shape_create( mode: 'rect' | 'circle', box: $shm_hitalama_map_pane_tool_box, operation = '', preview?: $shm_hitalama_map_pane_tool_save ) {
			const pos = $mol_wire_sync( Array ).of( box.left, box.top ) as number[]
			const initialize = $mol_wire_sync( this ).shape_initialize( preview )
			const widget = this.Map_page().map().widget_add(
				mode as $shm_hitalama_map_widget_def_type_name,
				pos,
				box.width,
				box.height,
				undefined, '', undefined, operation, initialize,
			)!
			this.Map_page().selected_links([ widget.link() ])
		}

		line_box( line: $shm_hitalama_map_widget_entity_drawing_line ) {
			const points = line.points
			if( points.length === 0 ) return null

			const pad = Math.max( 1, line.width ) / 2 + 2
			let left = Infinity
			let top = Infinity
			let right = -Infinity
			let bottom = -Infinity

			for( const point of points ) {
				left = Math.min( left, point[0] )
				top = Math.min( top, point[1] )
				right = Math.max( right, point[0] )
				bottom = Math.max( bottom, point[1] )
			}

			return {
				left: left - pad,
				top: top - pad,
				width: Math.max( 1, right - left + pad * 2 ),
				height: Math.max( 1, bottom - top + pad * 2 ),
			}
		}

		shape_box( default_size = false ): $shm_hitalama_map_pane_tool_box | null {
			const start = this.draft_start() as $shm_hitalama_map_pane_tool_point | null
			const end = this.draft_end() as $shm_hitalama_map_pane_tool_point | null
			if( !start || !end ) return null

			const left = Math.min( start[0], end[0] )
			const top = Math.min( start[1], end[1] )
			const width = Math.abs( end[0] - start[0] )
			const height = Math.abs( end[1] - start[1] )

			if( default_size && width < 2 && height < 2 ) {
				const size = this.mode() === 'circle' ? 120 : 160
				return {
					left: start[0],
					top: start[1],
					width: size,
					height: this.mode() === 'circle' ? size : 100,
				}
			}

			if( this.mode() === 'circle' ) {
				const size = Math.max( width, height, 1 )
				return {
					left: end[0] < start[0] ? start[0] - size : start[0],
					top: end[1] < start[1] ? start[1] - size : start[1],
					width: size,
					height: size,
				}
			}

			return {
				left,
				top,
				width: Math.max( 1, width ),
				height: Math.max( 1, height ),
			}
		}

		tool_sub() {
			return this.preview_jobs().length || this.enabled() && this.drafting() ? super.tool_sub() : []
		}

		draft_views() {
			const pending = this.preview_jobs().map( job => this.Pending( job ) )
			if( !this.drafting() ) return pending
			switch( this.mode() ) {
				case 'drawing': return [ ...pending, this.Stroke() ]
				case 'rect': return [ ...pending, this.Rect() ]
				case 'circle': return [ ...pending, this.Circle() ]
				default: return pending
			}
		}

		preview_path( job: $shm_hitalama_map_pane_tool_save ) {
			const snapshot = job.snapshot()
			if( !snapshot ) return ''
			if( snapshot.mode === 'drawing' ) return snapshot.line.points.map( (point,index) => `${ index ? 'L' : 'M' }${point.join(',')}` ).join(' ')
			const { left:x, top:y, width:w, height:h } = snapshot.box
			if( snapshot.mode === 'rect' ) return `M${x},${y} h${w} v${h} h${-w} Z`
			return `M${x},${y+h/2} a${w/2},${h/2} 0 1 0 ${w},0 a${w/2},${h/2} 0 1 0 ${-w},0 Z`
		}

		preview_color( job: $shm_hitalama_map_pane_tool_save ) {
			const snapshot = job.snapshot()
			return snapshot?.mode === 'drawing' ? snapshot.line.color : '#808080'
		}
		preview_width( job: $shm_hitalama_map_pane_tool_save ) {
			const snapshot = job.snapshot()
			return String( snapshot?.mode === 'drawing' ? snapshot.line.width : 4 )
		}
		preview_join( job: $shm_hitalama_map_pane_tool_save ) { return job.snapshot()?.mode === 'drawing' ? 'round' : 'miter' }

		stroke_points() {
			const line = this.draft_line() as $shm_hitalama_map_widget_entity_drawing_line | null
			return line?.points.map( point => point.join( ',' ) ).join( ' ' ) ?? ''
		}

		stroke_color() {
			const line = this.draft_line() as $shm_hitalama_map_widget_entity_drawing_line | null
			return line?.color ?? '#808080'
		}

		stroke_width() {
			const line = this.draft_line() as $shm_hitalama_map_widget_entity_drawing_line | null
			return String( line?.width ?? 4 )
		}

		shape_fill() {
			return 'transparent'
		}

		shape_x() {
			return String( this.shape_box()?.left ?? 0 )
		}

		shape_y() {
			return String( this.shape_box()?.top ?? 0 )
		}

		shape_width() {
			return String( this.shape_box()?.width ?? 0 )
		}

		shape_height() {
			return String( this.shape_box()?.height ?? 0 )
		}

		circle_cx() {
			const box = this.shape_box()
			return String( box ? box.left + box.width / 2 : 0 )
		}

		circle_cy() {
			const box = this.shape_box()
			return String( box ? box.top + box.height / 2 : 0 )
		}

		circle_rx() {
			return String( ( this.shape_box()?.width ?? 0 ) / 2 )
		}

		circle_ry() {
			return String( ( this.shape_box()?.height ?? 0 ) / 2 )
		}

		view_box() {
			const rect = this.Map_page().Pane().view_rect_ensured()
			if( !rect ) return '0 0 1 1'

			const pan = this.pan()
			const zoom = this.zoom()
			const left = ( 0 - pan.x ) / zoom
			const top = ( 0 - pan.y ) / zoom
			return `${ left } ${ top } ${ rect.width / zoom } ${ rect.height / zoom }`
		}

	}

}
